import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errText } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1605792657660-596af9009e82?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  useEffect(() => { api.get("/setup/state").then((r) => { if (r.data?.needs_setup) navigate("/setup", { replace: true }); }).catch(() => {}); }, [navigate]);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const res = await login(email, password);
      if (res?.requires_2fa) { setPending(res.pending_token); return; }
      navigate("/");
    } catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };
  const submitCode = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { await verify2fa(pending, code.trim()); navigate("/"); } catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };

  if (pending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0b0e11] p-6">
        <form onSubmit={submitCode} className="w-full max-w-sm fade-in" data-testid="twofa-form">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-7 h-7 rotate-45 bg-[#fcd535] rounded-[3px] flex items-center justify-center"><div className="w-2.5 h-2.5 bg-[#181a20] -rotate-45" /></div>
            <span className="font-display font-extrabold text-white text-xl">QUANT<span className="text-[#fcd535]">BOT</span></span>
          </div>
          <div className="flex items-center gap-2"><KeyRound size={16} className="text-[#fcd535]" /><h2 className="text-lg font-semibold text-white">Two-factor authentication</h2></div>
          <p className="text-xs text-[#848e9c] mt-1">{useRecovery ? "Enter one of your 8-character recovery codes." : "Enter the 6-digit code from Google Authenticator."}</p>
          <input className="input mt-6 mono text-center text-xl tracking-[0.3em]" value={code} onChange={(e) => setCode(useRecovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, "").slice(0, 6))} autoFocus autoComplete="one-time-code" inputMode={useRecovery ? "text" : "numeric"} placeholder={useRecovery ? "XXXX-XXXX" : "000000"} required data-testid="twofa-code-input" />
          {error && <div className="mt-3 text-xs text-[#f6465d] bg-[rgba(246,70,93,0.1)] border border-[#f6465d]/30 rounded px-3 py-2" data-testid="twofa-error-message">{error}</div>}
          <button type="submit" disabled={busy || code.length < 6} className="btn-yellow w-full rounded h-10 mt-6 text-sm" data-testid="twofa-submit-button">{busy ? "Verifying…" : "Verify"}</button>
          <button type="button" onClick={() => { setUseRecovery(!useRecovery); setCode(""); setError(""); }} className="btn-ghost w-full rounded h-9 mt-2 text-xs" data-testid="twofa-toggle-recovery">{useRecovery ? "Use authenticator code instead" : "Use a recovery code"}</button>
          <button type="button" onClick={() => { setPending(null); setCode(""); setError(""); }} className="w-full mt-4 text-[11px] text-[#5e6673] hover:text-white" data-testid="twofa-back-button">← Back to sign in</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0b0e11]">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO} alt="Crypto trading" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0b0e11]" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="text-[11px] tracking-[0.2em] text-[#fcd535] font-semibold">AI-POWERED · RISK-GATED · AUDITABLE</div>
          <h1 className="font-display text-4xl font-extrabold text-white mt-3 leading-tight">Institutional-grade<br />crypto trading terminal</h1>
          <p className="text-[#848e9c] mt-4 max-w-md text-sm">Multi-timeframe technical analysis, LLM market interpretation, a deterministic risk engine and paper/live execution — every decision traceable end to end.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm fade-in" data-testid="login-form">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-7 h-7 rotate-45 bg-[#fcd535] rounded-[3px] flex items-center justify-center"><div className="w-2.5 h-2.5 bg-[#181a20] -rotate-45" /></div>
            <span className="font-display font-extrabold text-white text-xl">QUANT<span className="text-[#fcd535]">BOT</span></span>
          </div>
          <h2 className="text-lg font-semibold text-white">Sign in</h2>
          <p className="text-xs text-[#848e9c] mt-1">Secure gateway · JWT session · brute-force protected</p>
          <label className="block mt-6 text-[11px] text-[#848e9c] uppercase tracking-wider">Email</label>
          <input className="input mt-1" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" data-testid="login-email-input" />
          <label className="block mt-4 text-[11px] text-[#848e9c] uppercase tracking-wider">Password</label>
          <input className="input mt-1" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" data-testid="login-password-input" />
          {error && <div className="mt-3 text-xs text-[#f6465d] bg-[rgba(246,70,93,0.1)] border border-[#f6465d]/30 rounded px-3 py-2" data-testid="login-error-message">{error}</div>}
          <button type="submit" disabled={busy} className="btn-yellow w-full rounded h-10 mt-6 text-sm" data-testid="login-submit-button">{busy ? "Signing in…" : "Sign in"}</button>
          <div className="flex items-center gap-2 mt-8 text-[11px] text-[#5e6673]"><ShieldCheck size={13} className="text-[#0ecb81]" /> Private keys and seed phrases are never requested. Exchange keys are encrypted at rest.</div>
        </form>
      </div>
    </div>
  );
}
